import React, { useRef, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { dateTimeFormatterReturnDateOnly } from "utilities";
import { useModal } from "context/ModalContext";
import { sendUserAccesscode, sendUserActivation, deleteUser } from "actions/Users";
import { submitNotificationPerformance, setReopenModal } from "actions/App";
import { forgotUserPassword } from 'actions/Auth';
import moment from "moment-timezone";
import UnitsUsersDetailModal from "app/routes/Sites/routes/Display/UnitsUsers/UnitsUsersDetailModal";
import UserSettingsModal from "app/routes/Sites/routes/Display/UnitsUsers/UserSettings/UserSettingsModal";
import Popout from 'components/Popout';
import PopoutDropdown from "components/PopoutDropdown";
import ButtonContained from "components/ButtonContained";
import ButtonIconOnly from "components/ButtonIconOnly";
import Icon from "components/Icon";

// Utility function to check if the activation date is valid and should be shown
const showActivateDate = (date) => {
  if (!date) return false;

  return !(date.includes('0001-01-01') || date.includes('1970-01-01'));
}

const UserDetailModal = ({ uuDetail, unitUser }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const popoutAnchorRef = useRef(null);
  const { openModal, closeModal } = useModal();
  const [isPopoutOpen, setIsPopoutOpen] = useState(false);
  const authUser = useSelector(state => state.auth.authUser);
  const site = useSelector(state => state.auth.site);
  const userTimezone = useSelector(state => state.app.userTimezone);
  const userKeypadStatus = useSelector(state => state.users.userKeypadStatus);

 const has5eLock = uuDetail?.unit?.locks && uuDetail.unit.locks.some(lock => lock.bleHwVersion && lock.bleHwVersion.includes('5E'));
  const expiration = has5eLock 
    ? moment().tz(site.timeZone).endOf('day').utc()
    : moment(uuDetail?.unit?.User?.offlineKeyExpiration);

  const isExpired = expiration.isBefore(moment());

  const showOptions = useMemo(() => {
    const { unit } = uuDetail;
    const { User } = unit;
    const { permissions } = authUser;
    const createdExternal = ["breeze", "sitelink", "storedge", "gds"].includes(User?.createdBy) || (site?.pms !== "" && User?.createdBy == "");
    const isServiceUser = (User?.role?.isServiceUser || unit.accessType === "service");
    const authUserTier = authUser?.role?.tier;
    const userTier = User?.role?.tier;
    const baseCheck = authUserTier === 1 || authUserTier <= userTier || userTier == 0; 

    return baseCheck &&
    ((!isServiceUser && !createdExternal && (permissions.includes('manage_tenants') || permissions.includes('manage_users_admin')) && userTier === 0) ||
    (!isServiceUser && permissions.includes('manage_employees') && authUserTier < userTier && userTier > 0) ||
    (!isServiceUser && permissions.includes('manage_users_admin') && userTier >= authUserTier) ||
    (isServiceUser && permissions.includes('manage_service_users')))
  }, [uuDetail, authUser, site]);

  const backupManageOption = useMemo(() => {
    const { permissions } = authUser;
    const userTier = uuDetail.unit.User.role.tier;
    return ((permissions.includes('manage_tenants') && userTier == 0) ||
    (permissions.includes('manage_employees') && userTier > 0))
  }, [uuDetail, authUser]);

  const handleResetPasswordEmail = () => {
    dispatch(submitNotificationPerformance({
      site_id: site.id,
      user_id: uuDetail.unit.User.id,
      notification_type: 'password_reset',
      notification_name: 'password_reset',
      start_time: moment().utc()
    }));
    dispatch(forgotUserPassword(
      {
        companyUUID: site.companyUUID,
        email: uuDetail.unit.User.email
      },
      {
        successMessage: intl.formatMessage({ id: "notifications.success.sent" }),
        failedMessage: intl.formatMessage({ id: "notifications.error.failed" })
      }
    ));
  };

  const handleResetPasswordPhone = () => {
    dispatch(submitNotificationPerformance({
      site_id: site.id,
      user_id: uuDetail.unit.User.id,
      notification_type: 'password_reset',
      notification_name: 'password_reset',
      start_time: moment().utc()
    }));
    dispatch(forgotUserPassword(
      {
        companyUUID: site.companyUUID,
        phone: uuDetail.unit.User.phone
      },
      {
        successMessage: intl.formatMessage({ id: "notifications.success.sentText" }),
        failedMessage: intl.formatMessage({ id: "notifications.error.failed" })
      }
    ));
  };

  const handleActivateUser = () => {
    dispatch(submitNotificationPerformance({
      site_id: site.id,
      user_id: uuDetail.unit.User.id,
      notification_type: 'account_activation',
      notification_name: 'new_account',
      start_time: moment().utc()
    }));
    dispatch(sendUserActivation(
      uuDetail.unit.User.uuid && uuDetail.unit.User.uuid !== "" ? uuDetail.unit.User.uuid : uuDetail.unit.User.id.toString(),
      false,
      {
        successMessage: intl.formatMessage({ id: "notifications.success.edit" }),
        failedMessage: intl.formatMessage({ id: "notifications.error.failed" })
      }
    ));
  };

  return (
    <div className="px-10 flex flex-col justify-between min-h-[300px]">
      <Popout
        isOpen={isPopoutOpen}
        onClose={() => setIsPopoutOpen(false)}
        anchorRef={popoutAnchorRef}
        positioning="bottom-left"
        header={false}>
        <div className="max-w-[300px] gap-3 flex flex-col pt-6">
          <p className="tw-paragraph-lg">{intl.formatMessage({ id: "comp.form.delete.description" })}</p>
          <p className="tw-paragraph-reg text-[var(--color-alert)]">{intl.formatMessage({ id: "comp.form.delete.user.warning" })}</p>
          <div className="flex gap-2 justify-start">
            <ButtonContained
              id="User-Detail-Modal-Confirm-Delete-Button"
              dataCy="User-Detail-Modal-Confirm-Delete-Button"
              text={intl.formatMessage({ id: "comp.button.delete" })}
              type="default"
              onClick={() => {
                dispatch(
                  deleteUser(
                    uuDetail.unit.User.uuid && uuDetail.unit.User.uuid !== "" ? uuDetail.unit.User.uuid : uuDetail.unit.User.id.toString(),
                    false,
                    false,
                    {
                      successMessage: intl.formatMessage({ id: "notifications.success.delete" }) })); setIsPopoutOpen(false); closeModal();
                    }
              }
            />
            <ButtonContained
              id="User-Detail-Modal-Cancel-Delete-Button"
              dataCy="User-Detail-Modal-Cancel-Delete-Button"
              text={intl.formatMessage({ id: "comp.button.cancel" })}
              type="defaultOutline"
              onClick={() => setIsPopoutOpen(false)}
            />
          </div>
        </div>
      </Popout>
      <div>
        <div className="flex justify-between mt-2 mb-8">
          <div className="flex flex-col">
            <h5 className="tw-heading-5 mb-2">{uuDetail.unit.id ? uuDetail.unit.User.fullName : ""}</h5>
            <div className="flex gap-8">
              {uuDetail.unit.User.email && uuDetail.unit.User.email !== "" && <div className="flex items-center gap-2">
                <div className="flex justify-center items-center rounded-full w-8 h-8 bg-[var(--color-gray-100)]">
                  <Icon
                    name="mail"
                    size={16}
                    className="text-[var(--color-gray-400)]"
                  />
                </div>
                <p className="tw-paragraph-lg text-[var(--color-gray-800)]">
                  {uuDetail.unit.User.email}
                </p>
              </div>}
              {uuDetail.unit.User.parsedPhone && uuDetail.unit.User.parsedPhone !== "" && <div className="flex items-center gap-2">
                <div className="flex justify-center items-center rounded-full w-8 h-8 bg-[var(--color-gray-100)]">
                  <Icon
                    name="phone"
                    size={16}
                    className="text-[var(--color-gray-400)]"
                  />
                </div>
                <p className="tw-paragraph-lg text-[var(--color-gray-800)]">
                  {uuDetail.unit.User.parsedPhone}
                </p>
              </div>}
            </div>
          </div>
          <div className="flex gap-2">
            {(showOptions || backupManageOption || (authUser.id === uuDetail.unit.User.id)) && <ButtonIconOnly
              id="User-Detail-Modal-Settings-Icon"
              dataCy="User-Detail-Modal-Settings-Icon"
              icon="settings"
              type="default"
              onClick={() => {
                openModal(
                  <UserSettingsModal
                    uuDetail={uuDetail}
                  />,
                  (
                    <div className="flex gap-2 items-center">
                      <ButtonIconOnly
                        id="User-Settings-Modal-Back-Button"
                        dataCy="User-Settings-Modal-Back-Button"
                        icon="arrowLeft"
                        type="default"
                        onClick={() => openModal(
                          <UnitsUsersDetailModal
                            unitUser={unitUser}
                          />
                        )}
                      />
                      <h4 className="tw-heading-4 text-[var(--color-gray-800)]">{`${uuDetail.unit.User.fullName} - ${intl.formatMessage({ id: "pages.settings.Types" })}`}</h4>
                    </div>
                  )
                );
              }}
            />}
            {/* We shouldn't let the user delete themselves */}
            {showOptions && authUser.id !== uuDetail.unit.User.id && <ButtonIconOnly
              ref={popoutAnchorRef}
              id="User-Detail-Modal-Delete-Icon"
              dataCy="User-Detail-Modal-Delete-Icon"
              icon="trash"
              type="default"
              customColor="text-[var(--color-alert)]"
              onClick={() => setIsPopoutOpen(true)}
            />}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="flex flex-col gap-2">
            <p className="tw-desc-reg font-semibold text-[var(--color-gray-400)] uppercase">{intl.formatMessage({ id: "form.created" })}</p>
            <p className="tw-paragraph-lg text-[var(--color-gray-800)]">{dateTimeFormatterReturnDateOnly(uuDetail?.unit?.User?.createdDate, authUser, site, userTimezone)}</p>
          </div>
          <div className="flex flex-col gap-2">
            <p className="tw-desc-reg font-semibold text-[var(--color-gray-400)] uppercase">{intl.formatMessage({ id: "form.createdBy" })}</p>
            <p className="tw-paragraph-lg text-[var(--color-gray-800)]">{uuDetail?.unit?.User?.createdBy}</p>
          </div>
          <div className="flex flex-col gap-2">
            <p className="tw-desc-reg font-semibold text-[var(--color-gray-400)] uppercase">{intl.formatMessage({ id: "form.activated" })}</p>
            <p className="tw-paragraph-lg text-[var(--color-gray-800)]">{showActivateDate(uuDetail?.unit?.User?.firstLoginDate) ? dateTimeFormatterReturnDateOnly(uuDetail?.unit?.User?.firstLoginDate, authUser, site, userTimezone) : intl.formatMessage({ id: "form.na" })}</p>
          </div>
          <div className="flex flex-col gap-2">
            <p className="tw-desc-reg font-semibold text-[var(--color-gray-400)] uppercase">{intl.formatMessage({ id: "pages.schedules.Type" })}</p>
            <p className="tw-paragraph-lg text-[var(--color-gray-800)]">{uuDetail?.unit?.User?.role?.userSiteHoursSuiteName === "" ? intl.formatMessage({ id: "pages.schedules.defaultShort" }) : uuDetail?.unit?.User?.role?.userSiteHoursSuiteName}</p>
          </div>
          <div className="flex flex-col gap-2">
            <p className="tw-desc-reg font-semibold text-[var(--color-gray-400)] uppercase">{intl.formatMessage({ id: "form.accessCode" })}</p>
            <p className="tw-paragraph-lg text-[var(--color-gray-800)]">{uuDetail?.unit?.accessCodes?.[0].accessCode || "--"}</p>
          </div>
          <div className="flex flex-col gap-2">
            <p className="tw-desc-reg font-semibold text-[var(--color-gray-400)] uppercase">{intl.formatMessage({ id: "form.offlineKey" })}</p>
            <p className="tw-paragraph-lg text-[var(--color-gray-800)]">
              {isExpired
                ? intl.formatMessage({ id: "form.expired" })
                : moment
                    .duration(expiration.tz(site.timeZone).utc().diff(moment()))
                    .humanize()
              }
            </p>
          </div>
          {/* only show expiration date if it is after 2000-01-01 */}
          {uuDetail?.unit?.User?.expiration && moment(uuDetail?.unit?.User?.expiration).isAfter('2000-01-01') && (
            <div className="flex flex-col gap-2">
              <p className="tw-desc-reg font-semibold text-gray-400 uppercase">{intl.formatMessage({ id: 'comp.repeatSchedule.expirationDate' })}</p>
              <p className="tw-paragraph-lg text-(--color-gray-800)">{dateTimeFormatterReturnDateOnly(uuDetail?.unit?.User?.expiration, authUser, site, userTimezone)}</p>
            </div>
          )}
        </div>

        {/* NEED TO FILTER OUT SERVICE UNITS POSSIBLY??? */}
        {uuDetail?.unit?.User?.units?.filter(u => u.accessType !== "service").length > 1 && <div>
          <div className="flex flex-col gap-2 mb-8">
            <p className="tw-desc-reg font-semibold text-[var(--color-gray-400)] uppercase">{intl.formatMessage({ id: "form.otherUnits" })}</p>
            <div className="flex flex-wrap gap-2">
              {uuDetail?.unit?.User?.units?.filter(u => (u.id !== uuDetail.unit.id) && u.accessType !== "service").map(unit => (
                <span
                  key={unit.id}
                  onClick={() => dispatch(setReopenModal({ unitUUID: unit.id }))}
                  className="inline-block w-fit py-3 px-4 bg-[var(--color-gray-50)] rounded-[8px] cursor-pointer"
                >
                  <p className="tw-paragraph-lg font-semibold inline">{unit.name}</p>
                </span>
              ))}
            </div>
          </div>
        </div>}
      </div>

      <div className="flex gap-2 justify-start">
        {(authUser.permissions?.includes('manage_access_codes') &&
        (site.has3K || site.has4K || (site.hasKeypad > 0)) &&
        userKeypadStatus) && <ButtonContained
          id="User-Detail-Modal-Resend-Access-Code-Button"
          dataCy="User-Detail-Modal-Resend-Access-Code-Button"
          text={intl.formatMessage({ id: "form.resendAccessCode" })}
          type="default"
          onClick={() => {
            dispatch(sendUserAccesscode(
              uuDetail.unit.User.uuid,
              {
                failedMessage: intl.formatMessage({ id: "notifications.error.failed" }),
                successMessage: intl.formatMessage({ id: "notifications.success.sentAccessCodes" })
              }
            ));
          }}
        />}
        <ButtonContained
          id="User-Detail-Modal-View-Activity-Button"
          dataCy="User-Detail-Modal-View-Activity-Button"
          text={intl.formatMessage({ id: "comp.table.cell.viewActivity" })}
          type="defaultOutline"
          onClick={() => {
            closeModal();
            history.push(
              {
                pathname: "activity",
                state: {
                  predefinedFilters:
                    {
                      userUUID: uuDetail.unit.User.uuid || uuDetail.unit.User.id.toString()
                    }
                }
              }
            );
          }}
        />
        {(((authUser.permissions.includes("manage_tenants") && uuDetail.unit.User.role.tier === 0) ||
          (authUser.permissions.includes("manage_employees") && uuDetail.unit.User.role.tier > 0)) &&
          (site?.hideUserPasswordActivate == "false") &&
          (uuDetail?.unit?.User?.loginType === "" || uuDetail?.unit?.User?.loginType === "noke")) &&
          (
            uuDetail.unit.User.phone !== "" && uuDetail.unit.User.email !== ""
            ?
            <PopoutDropdown
              id="User-Detail-Modal-Reset-Password-Dropdown-Button"
              dataCy="User-Detail-Modal-Reset-Password-Dropdown-Button"
              label={intl.formatMessage({ id: "comp.button.recoverPassword" })}
              direction="up"
              align="left"
              largeButton={true}
              buttonIcon={null}
              options={
                [
                  {
                    value: "email",
                    name: intl.formatMessage({ id: "comp.button.sendEmail" })
                  },
                  {
                    value: "phone",
                    name: intl.formatMessage({ id: "comp.button.sendText" })
                  }
                ]
              }
              onOptionSelect={(option) => option === "phone" ? handleResetPasswordPhone() : handleResetPasswordEmail()}
            />
            :
            <ButtonContained
              id="User-Detail-Modal-Reset-Password-Button"
              dataCy="User-Detail-Modal-Reset-Password-Button"
              text={intl.formatMessage({ id: "comp.button.recoverPassword" })}
              type="defaultOutline"
              onClick={() => uuDetail.unit.User.phone !== "" ? handleResetPasswordPhone() : handleResetPasswordEmail()}
            />
          )
        }
        {(((authUser.permissions.includes("manage_tenants") && uuDetail.unit.User.role.tier === 0) ||
          (authUser.permissions.includes("manage_employees") && uuDetail.unit.User.role.tier > 0)) &&
          (site?.hideUserPasswordActivate == "false") &&
          (uuDetail?.unit?.User?.loginType === "" || uuDetail?.unit?.User?.loginType === "noke")) &&
          <ButtonContained
            id="User-Detail-Modal-Activate-Button"
            dataCy="User-Detail-Modal-Activate-Button"
            text={intl.formatMessage({ id: "comp.button.activate" })}
            type="defaultOutline"
            onClick={() => handleActivateUser()}
          />
        }
      </div>
    </div>
  )
}

export default UserDetailModal;